import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import PropTypes from 'prop-types';
import * as conferenceActions from '@actions/conference';
import { MeetingSessionConfiguration } from 'amazon-chime-sdk-js';
import { useMeetingManager } from 'amazon-chime-sdk-component-library-react';
import * as globalActions from '@actions/global';
import { isSidebarVisibleSelector } from '@selectors/global';
import {
  meetingSelector,
  attendeeInfoSelector,
  activeConferenceSelector,
  conferencesSelector,
  conferencesPaginationSelector,
  pastConferencesSelector,
  pastConferencesPaginationSelector,
  helpSessionsSelector,
  helpSessionsPaginationSelector,
  pastHelpSessionsSelector,
  pastHelpSessionsPaginationSelector,
  conferenceMetaSelector,
  endMeetingOptionsSelector,
  meetingPermissionsChecksSelector
} from '@selectors/conference';

const ConferencingContext = React.createContext(
  null
);

export function getConferencingContext() {
  return ConferencingContext;
}

export default function ConferencingProvider({ children, isHelpDesk }) {
  const ConferenceContext = getConferencingContext();
  const dispatch = useDispatch();
  const meetingManager = useMeetingManager();
  const history = useHistory();

  const isSidebarVisible = useSelector(isSidebarVisibleSelector);
  const meeting = useSelector(meetingSelector);
  const attendeeInfo = useSelector(attendeeInfoSelector);
  const conference = useSelector(activeConferenceSelector);
  const conferences = isHelpDesk ? useSelector(helpSessionsSelector) : useSelector(conferencesSelector);
  const pastConferences = isHelpDesk ? useSelector(pastHelpSessionsSelector) : useSelector(pastConferencesSelector);
  const pagination = isHelpDesk ? useSelector(helpSessionsPaginationSelector) : useSelector(conferencesPaginationSelector);
  const pastPagination = isHelpDesk ? useSelector(pastHelpSessionsPaginationSelector) : useSelector(pastConferencesPaginationSelector);
  const meta = useSelector(conferenceMetaSelector);
  const endMeetingOptions = useSelector(endMeetingOptionsSelector);
  const permissions = useSelector(meetingPermissionsChecksSelector);

  const toggleSidebar = async () => {
    await dispatch(globalActions.toggleSidebar());
  };

  const [viewState, setViewState] = useState({
    started: false,
    sideBar: {
      visible: isSidebarVisible,
      toggleSidebar
    },
    options: {
      canChange: true,
      visible: false,
      blurStrength: 20,
    }
  });

  const changeViewState = async (target, value) => {
    await setViewState(currentViewState => {
      return {
        ...currentViewState,
        [target]: value,
      };
    });
  };

  const toggleOptions = async () => {
    await setViewState(currentViewState => {
      var options = currentViewState.options;
      return {
        ...currentViewState,
        options: {
          ...options,
          visible: !options.visible
        }
      };
    });
  };

  const setBlurStrength = async (blurStrength) => {
    await setViewState(currentViewState => {
      var options = currentViewState.options;
      return {
        ...currentViewState,
        options: {
          ...options,
          blurStrength: blurStrength
        }
      };
    });
  };

  const [hearingState, setHearingState] = useState({});

  useEffect(() => {
    if (meeting && meeting?.conferenceID && !hearingState[meeting?.conferenceID]) {
      setHearingState(hearingState => ({
        ...hearingState,
        [meeting?.conferenceID]: {
          started: false,
          refreshProperty: false,
          lastModified: new Date().getTime()
        },
      }));
    }
  }, [meeting]);

  const changeHearingState = useCallback((conferenceID, target, value) => {
    setHearingState(hearingState => ({
      ...hearingState,
      [conferenceID]: {
        ...hearingState[conferenceID],
        [target]: value,
        lastModified: new Date().getTime()
      },
    }));
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      const currentTime = new Date().getTime();
      setHearingState(hearingState => {
        const newState = { ...hearingState };
        Object.keys(newState).forEach(conferenceID => {
          if (currentTime - newState[conferenceID].lastModified > 30 * 60 * 1000) { // 30 minutes
            delete newState[conferenceID];
          }
        });
        return newState;
      });
    }, 60 * 1000);

    return () => clearInterval(interval);
  }, []);

  const getConferences = async (meetingType) => {
    if (isHelpDesk) {
      await dispatch(conferenceActions.fetchCurrentHelpSessions({
        page: pagination.page,
        pageSize: pagination.rowsPerPage
      }));
    } else {
      await dispatch(conferenceActions.fetchCurrentConferences({
        page: pagination.page,
        pageSize: pagination.rowsPerPage,
        meetingType: meetingType
      }));
    }
  };

  const getPastConferences = async (meetingType) => {
    if (isHelpDesk) {
      await dispatch(conferenceActions.fetchPastHelpSessions({
        page: pagination.page,
        pageSize: pagination.rowsPerPage,
        meetingType: meetingType
      }));
    } else {
      await dispatch(conferenceActions.fetchPastConferences({
        page: pagination.page,
        pageSize: pagination.rowsPerPage,
        meetingType: meetingType
      }));
    }
  };

  const getAttendeeName = async (conferenceID, attendeeId) => {
    try {
      const res = await dispatch(conferenceActions.fetchAttendeeById({ conferenceID, attendeeId }));
      console.log(res, 'Attendee Information');
      if (res && res?.results) {
        return res?.results?.Name;
      }
      return '';
    } catch (err) {
      console.log(err, 'Conference Provider');
      return '';
    }
  };

  const getAttendee = async (conferenceID, attendeeId) => {
    try {
      const res = await dispatch(conferenceActions.fetchAttendeeById({ conferenceID, attendeeId }));
      if (res && res?.results) {
        return res?.results;
      }
      return {};
    } catch (err) {
      console.log(err, 'Conference Provider');
      return {};
    }
  };

  const handleFetchMeeting = async (conferenceID, connectOptions, meetingID, region, joinInfo) => {
    try {
      const attendee = {
        attendeeId: joinInfo?.attendee?.Attendee?.AttendeeId,
        ...joinInfo?.attendee?.Attendee
      };
      const externalUserId = joinInfo?.attendee?.Attendee?.ExternalUserId;
      const attendeeData = await getAttendee(conferenceID, joinInfo?.attendee?.Attendee?.AttendeeId);
      const resPhone = await dispatch(conferenceActions.fetchSpportCallInNumber());
      const phoneNumber = resPhone?.results?.phoneNumber || '';
      const meetingSessionConfiguration = new MeetingSessionConfiguration(joinInfo?.meeting?.Meeting, attendee);

      await meetingManager.join(meetingSessionConfiguration);
      const data = {
        conferenceID,
        id: meetingID.trim().toLocaleLowerCase(),
        attendeeName: attendeeData.Name.trim() || externalUserId,
        phoneNumber,
        region,
        attendeeInfo: attendeeData
      };
      await dispatch(conferenceActions.setAWSChimesMeeting(data));
      const options = {
        url: `${connectOptions.endMeetingUrl}`
      };
      await dispatch(conferenceActions.setEndMeetingOptions(options));

      history.push(`${connectOptions.deviceSetupUrl}`);
    } catch (err) {
      console.log(err, 'Conference Provider');
      throw err;
    }
  };

  const joinConference = async (conferenceID, deviceSetupUrl, endMeetingUrl) => {
    try {
      const { results } = await dispatch(
        conferenceActions.joinChimeConference({ conferenceID }),
      );
      if (results) {
        await handleFetchMeeting(
          conferenceID,
          {
            deviceSetupUrl,
            endMeetingUrl
          },
          results.meeting.Meeting.MeetingId,
          results.meeting.Meeting.MediaRegion,
          results);
      }
    } catch (err) {
      console.log(err, 'Conference Provider');
      throw err;
    }
  };

  const refreshAttendeeInfo = async () => {
    if (!meeting) {
      return;
    }
    const attendeeData = await getAttendee(meeting.conferenceID, meeting.attendeeInfo.AttendeeId);
    await dispatch(conferenceActions.updateMeetingAttendeeInfo(attendeeData));
  };

  const downloadRecording = async (conferenceID, recordingS3ID) => {
    await dispatch(conferenceActions.downloadConferenceRecording({
      conferenceID: conferenceID,
      s3ID: recordingS3ID
    }));
  };

  const changePage = async (page) => {
    await dispatch(conferenceActions.changePage(page));
  };

  const changeRowsPerPage = async (rowsPerPage) => {
    await dispatch(conferenceActions.changeRowsPerPage(rowsPerPage));
  };

  const changePastPage = async (page) => {
    if (isHelpDesk) {
      await dispatch(conferenceActions.changePastHelpSessionsPage(page));
    } else {
      await dispatch(conferenceActions.changePastPage(page));
    }
  };

  const changePastRowsPerPage = async (rowsPerPage) => {
    if (isHelpDesk) {
      await dispatch(conferenceActions.changePastHelpSessionsRowsPerPage(rowsPerPage));
    } else {
      await dispatch(conferenceActions.changePastRowsPerPage(rowsPerPage));
    }
  };

  const getConference = async (conferenceID) => {
    const { results } = await dispatch(conferenceActions.fetchChimeConference({ conferenceID: conferenceID }));
    return results;
  };

  const setActiveConference = async (activeConference) => {
    await dispatch(conferenceActions.setActiveConference(activeConference));
  };

  const initAWSChimesChat = async () => {
    await dispatch(conferenceActions.initAWSChimesChat([]));
  };

  const resetEndMeeting = async () => {
    await dispatch(conferenceActions.setEndMeetingOptions({}));
    changeHearingState('started', false);
  };

  const endChimeConference = async (conferenceID, meetingType, propertyAction = 'All') => {
    localStorage.removeItem('selectedCase');
    localStorage.removeItem('permissions');
    if ((meetingType === 'Formal' || meetingType === 'Informal') && conference.panelID) {
      const payload = {
        checkInID: conference.checkInID,
        propertyAction: propertyAction
      };
      await dispatch(conferenceActions.endAppealPanelConference({ panelID: conference.panelID, payload }));
      await dispatch(conferenceActions.endChimeConference({ conferenceID: conferenceID }));
    } else {
      await dispatch(conferenceActions.endChimeConference({ conferenceID: conferenceID }));
    }
    changeHearingState('started', false);
  };

  const removeAttendee = async (conferenceID, attendeeId) => {
    return await dispatch(conferenceActions.removeAttendeeFromConference({ conferenceID, attendeeId }));
  };

  const updateAttendeeCapabilities = async (conferenceID, attendeeId, audio = false, content = false, video = false) => {
    const payload = {
      Audio: audio ? 'SendReceive' : 'Receive',
      Content: content ? 'SendReceive' : 'Receive',
      Video: video ? 'SendReceive' : 'Receive'
    }
    return await dispatch(conferenceActions.updateAttendeeCapabilities({ conferenceID, attendeeId, payload }));
  };

  const fetchMeetingInformation = async (conferenceID) => {
    return await dispatch(conferenceActions.fetchMeetingInformation({ conferenceID }));
  };

  const fetchPermissionChecks = async (conferenceID) => {
    await dispatch(conferenceActions.fetchPermissionChecks({ conferenceID }));
  }

  const providerValue = {
    meeting,
    conference,
    conferences,
    pastConferences,
    meetingManager,
    meta,
    initAWSChimesChat,
    endChimeConference,
    endMeetingOptions,
    resetEndMeeting,
    fetchMeetingInformation,
    paging: {
      pagination,
      changePage,
      changeRowsPerPage
    },
    pastPaging: {
      pagination: pastPagination,
      changePage: changePastPage,
      changeRowsPerPage: changePastRowsPerPage
    },
    downloadRecording,
    attendeeInfo,
    refreshAttendeeInfo,
    getAttendee,
    getAttendeeName,
    removeAttendee,
    updateAttendeeCapabilities,
    getConference,
    getConferences,
    getPastConferences,
    joinConference,
    setActiveConference,
    viewState,
    changeViewState,
    toggleOptions,
    setBlurStrength,
    permissions,
    fetchPermissionChecks,
    hearingState,
    changeHearingState
  };
  return (
    <ConferenceContext.Provider value={providerValue}>
      {children}
    </ConferenceContext.Provider>
  );
};

ConferencingProvider.propTypes = {
  children: PropTypes.any,
  isHelpDesk: PropTypes.bool.isRequired
};

ConferencingProvider.defaultProps = {
  isHelpDesk: false
}

const useConferencing = () => {
  const context = useContext(ConferencingContext);
  if (!context) {
    throw Error('Use useConferencing in ConferenceProvider');
  }
  return context;
};

export { ConferencingProvider, useConferencing };